import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { InvitationPage } from 'app/pages/User/InvitationsPage/InvitationPage';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { createFetchInvitationsQueryFn } from './api/fetchInvitations';

import { MainContent } from './styles/MainContent';

const SEARCH_DEBOUNCE_MS = 300;

export const InvitationsPage = () => {
  const { call } = useAPI();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { organization } = useAuthContext();
  const organizationId = organization?.id ?? '';
  const { openModal, closeModal } = useModalContext();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;

  const shouldShowReports = !!organization?.permissions?.viewReports;

  const invitationsQuery = useQuery({
    queryKey: organizationsKeys.invitationsList(organizationId, { search: debouncedSearch }),
    queryFn: createFetchInvitationsQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].organizationId === organizationId ? previousData : undefined,
    enabled: organizationId !== '' && !isSearchSettling,
  });

  const invalidateInvitations = () =>
    queryClient.invalidateQueries({ queryKey: organizationsKeys.invitationsByOrg(organizationId) });

  const openInviteUserModal = () => {
    openModal(ModalNamesEnum.InviteUserModal, {
      onSuccess: () => {
        closeModal();
        void invalidateInvitations();
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Invitations - VoiceScript</title>
      </Helmet>
      <Header
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              onClick={() => {
                navigate(routes.organizationMembers.make(organization?.slug ?? ''));
              }}
            >
              Members
            </SwitchButton>
            <SwitchButton
              active
              onClick={() => {
                navigate(routes.invitations.make(organization?.slug ?? ''));
              }}
            >
              Invitations
            </SwitchButton>
            {shouldShowReports ? (
              <SwitchButton
                onClick={() => {
                  navigate(routes.organizationMemberReports.make(organization?.slug ?? ''));
                }}
              >
                Reports
              </SwitchButton>
            ) : null}
            {shouldShowReports && organization?.featureFlags.ATTORNEY_VIEW_AVAILABLE ? (
              <SwitchButton
                onClick={() => {
                  navigate(routes.organizationAttorneysReport.make(organization?.slug ?? ''));
                }}
              >
                Attorneys Report
              </SwitchButton>
            ) : null}
          </HeaderSwitchWrapper>,
        ]}
        title="Invitations"
        setSearch={setSearch}
        search={search}
        buttonLabel={'Invite Member'}
        buttonOnClick={() => openInviteUserModal()}
      />

      <LinearProgressLoader active={invitationsQuery.isFetching || isSearchSettling} />
      <MainContent>
        {invitationsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load invitations.</span>
            <TextButton type="button" onClick={() => void invitationsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        <InvitationPage invitations={invitationsQuery.data ?? []} onInvitationsChange={invalidateInvitations} />
      </MainContent>
    </>
  );
};
